import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import { Input } from "@shadcn-in-nx/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { Button } from "@shadcn-in-nx/ui/button";
import { Search, X, Filter } from "lucide-react";
import Pagination from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import PlanRestrictionAlert from "../Chat/ChatTwoColumnPage/PlanRestrictionAlert";
import InternalLink from "../components/InternalLink";

export default function AuditLogs() {
    const {
        isSuperAdmin,
        isAdmin,
        isFeatureEnabled,
        getCustomPlanNumberFeature,
    } = useAuth();
    const isAuditLogsEnabled = isFeatureEnabled("auditLogs");
    const retentionDays = getCustomPlanNumberFeature("auditLogRetentionDays");
    const isLiteAudit = isAuditLogsEnabled && retentionDays < 999999;
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    // Search and filter state
    const [searchText, setSearchText] = useState("");
    const [action, setAction] = useState("all");
    const [actionCategory, setActionCategory] = useState("all");
    const [status, setStatus] = useState("all");
    const [severity, setSeverity] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Filter options from API
    const [filterOptions, setFilterOptions] = useState({
        actions: [],
        categories: [],
        statuses: [],
        severities: [],
    });

    const {
        call: getAuditLogs,
        isLoading,
        response,
    } = useBluelagoon({
        method: "getAuditLogs",
    });

    const { call: getAuditFilters, response: filtersResponse } = useBluelagoon({
        method: "getAuditFilters",
    });

    const fetchAuditLogs = useCallback(
        (pageOverride = null) => {
            // Only fetch if feature is enabled
            if (!isAuditLogsEnabled) {
                return;
            }

            const params = {
                page: pageOverride !== null ? pageOverride : currentPage,
                pageSize,
            };

            if (searchText) params.searchText = searchText;
            if (action && action !== "all") params.action = action;
            if (actionCategory && actionCategory !== "all")
                params.actionCategory = actionCategory;
            if (status && status !== "all") params.status = status;
            if (severity && severity !== "all") params.severity = severity;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            getAuditLogs(params);
        },
        [
            currentPage,
            pageSize,
            searchText,
            action,
            actionCategory,
            status,
            severity,
            startDate,
            endDate,
            getAuditLogs,
            isAuditLogsEnabled,
        ]
    );

    // Fetch filter options on mount
    useEffect(() => {
        getAuditFilters({});
    }, [getAuditFilters]);

    // Update filter options when API response arrives
    useEffect(() => {
        if (filtersResponse?.data) {
            setFilterOptions({
                actions: filtersResponse.data.actions || [],
                categories: filtersResponse.data.categories || [],
                statuses: filtersResponse.data.statuses || [],
                severities: filtersResponse.data.severities || [],
            });
        }
    }, [filtersResponse]);

    const handleSearch = () => {
        if (!isAuditLogsEnabled) {
            return;
        }
        setCurrentPage(1);
        // Pass page 1 directly to avoid stale state issue
        fetchAuditLogs(1);
    };

    const handlePageChange = (newPage) => {
        if (!isAuditLogsEnabled) {
            return;
        }
        setCurrentPage(newPage);
        // Pass newPage directly to avoid stale state issue
        fetchAuditLogs(newPage);
    };

    const handleClearFilters = () => {
        setSearchText("");
        setAction("all");
        setActionCategory("all");
        setStatus("all");
        setSeverity("all");
        setStartDate("");
        setEndDate("");
        setCurrentPage(1);
    };

    // Only allow admin users
    if (!isAdmin && !isSuperAdmin) {
        return <Navigate to="/profile" replace />;
    }

    const logs = response?.data?.logs || [];
    const pagination = response?.data?.pagination || {};

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case "critical":
                return "text-red-600 font-semibold";
            case "high":
                return "text-orange-600 font-semibold";
            case "medium":
                return "text-yellow-600";
            case "low":
                return "text-blue-600";
            default:
                return "text-gray-600";
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "success":
                return "text-green-600";
            case "failure":
                return "text-red-600";
            case "warning":
                return "text-yellow-600";
            default:
                return "text-gray-600";
        }
    };

    const hasActiveFilters =
        searchText ||
        (action && action !== "all") ||
        (actionCategory && actionCategory !== "all") ||
        (status && status !== "all") ||
        (severity && severity !== "all") ||
        startDate ||
        endDate;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold tracking-tight">
                    Audit Logs
                </h2>
            </div>

            {isLiteAudit && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Showing last {retentionDays} days of audit logs. Upgrade to
                    Enterprise Plus for full history.
                </div>
            )}

            {/* Search and Filters */}
            <div className="space-y-4 bg-card border border-border rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                        Search & Filters
                    </span>
                    {hasActiveFilters && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClearFilters}
                            className="ml-auto"
                        >
                            <X className="h-4 w-4 mr-1" />
                            Clear
                        </Button>
                    )}
                </div>

                {/* Search Bar */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by description, user email, user name, or resource name..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && isAuditLogsEnabled) {
                                handleSearch();
                            }
                        }}
                        disabled={!isAuditLogsEnabled}
                        className="pl-9"
                    />
                </div>

                {/* Filter Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                        <label className="text-sm font-medium mb-1 block">
                            Action
                        </label>
                        <Select
                            value={action}
                            onValueChange={setAction}
                            disabled={!isAuditLogsEnabled}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="All actions" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All actions</SelectItem>
                                {filterOptions.actions.map((actionValue) => (
                                    <SelectItem
                                        key={actionValue}
                                        value={actionValue}
                                    >
                                        {actionValue
                                            .replace(/_/g, " ")
                                            .toLowerCase()
                                            .replace(/\b\w/g, (l) =>
                                                l.toUpperCase()
                                            )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="text-sm font-medium mb-1 block">
                            Category
                        </label>
                        <Select
                            value={actionCategory}
                            onValueChange={setActionCategory}
                            disabled={!isAuditLogsEnabled}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="All categories" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All categories
                                </SelectItem>
                                {filterOptions.categories.map(
                                    (categoryValue) => (
                                        <SelectItem
                                            key={categoryValue}
                                            value={categoryValue}
                                        >
                                            {categoryValue
                                                .replace(/_/g, " ")
                                                .toLowerCase()
                                                .replace(/\b\w/g, (l) =>
                                                    l.toUpperCase()
                                                )}
                                        </SelectItem>
                                    )
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="text-sm font-medium mb-1 block">
                            Status
                        </label>
                        <Select
                            value={status}
                            onValueChange={setStatus}
                            disabled={!isAuditLogsEnabled}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="All statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All statuses
                                </SelectItem>
                                {filterOptions.statuses.map((statusValue) => (
                                    <SelectItem
                                        key={statusValue}
                                        value={statusValue}
                                    >
                                        {statusValue.charAt(0) +
                                            statusValue.slice(1).toLowerCase()}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="text-sm font-medium mb-1 block">
                            Severity
                        </label>
                        <Select
                            value={severity}
                            onValueChange={setSeverity}
                            disabled={!isAuditLogsEnabled}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="All severities" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All severities
                                </SelectItem>
                                {filterOptions.severities.map(
                                    (severityValue) => (
                                        <SelectItem
                                            key={severityValue}
                                            value={severityValue}
                                        >
                                            {severityValue.charAt(0) +
                                                severityValue
                                                    .slice(1)
                                                    .toLowerCase()}
                                        </SelectItem>
                                    )
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-end">
                        <Button
                            onClick={handleSearch}
                            className="w-full"
                            disabled={isLoading || !isAuditLogsEnabled}
                        >
                            <Search className="h-4 w-4 mr-2" />
                            Search
                        </Button>
                    </div>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-medium mb-1 block">
                            Start Date
                        </label>
                        <Input
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            disabled={!isAuditLogsEnabled}
                            min={
                                isLiteAudit
                                    ? new Date(
                                          Date.now() - retentionDays * 86400000
                                      )
                                          .toISOString()
                                          .slice(0, 16)
                                    : undefined
                            }
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-1 block">
                            End Date
                        </label>
                        <Input
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            disabled={!isAuditLogsEnabled}
                        />
                    </div>
                </div>
            </div>

            {/* Upgrade Message */}
            {!isAuditLogsEnabled && (
                <PlanRestrictionAlert
                    titleKey="auditLogs.upgradeRequired"
                    titleFallback="Upgrade Required"
                    descriptionKey="auditLogs.upgradeMessage"
                    descriptionFallback="Audit logs are available on Visionary plan and above. Upgrade your plan to view and search through audit logs."
                    variant="inline"
                />
            )}

            {/* Results */}
            <div className="border rounded-lg">
                {!isAuditLogsEnabled ? (
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                        <div className="text-center space-y-4">
                            <div className="text-muted-foreground text-lg">
                                Audit logs are not available on your current
                                plan
                            </div>
                            <p className="text-sm text-muted-foreground max-w-md">
                                Upgrade to Innovator plan or above to access
                                comprehensive audit logging and search
                                capabilities.
                            </p>
                            <InternalLink
                                to="/billing"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors mt-4"
                            >
                                <span>Upgrade Plan</span>
                            </InternalLink>
                        </div>
                    </div>
                ) : isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="text-muted-foreground">
                            Loading audit logs...
                        </div>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="text-muted-foreground">
                            No audit logs found
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Timestamp</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead>Action</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Severity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell className="text-sm">
                                            {formatDate(log.timestamp)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            <div>
                                                {log.user ? (
                                                    <>
                                                        <div className="font-medium">
                                                            {log.user.firstName}{" "}
                                                            {log.user.lastName}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {log.user.email}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-muted-foreground">
                                                        {log.userName ||
                                                            log.userEmail ||
                                                            "Unknown"}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            <div>
                                                <div className="font-medium">
                                                    {log.action}
                                                </div>
                                                {log.actionCategory && (
                                                    <div className="text-xs text-muted-foreground">
                                                        {log.actionCategory}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm max-w-md">
                                            <div
                                                className="truncate"
                                                title={log.description}
                                            >
                                                {log.description}
                                            </div>
                                            {log.resourceName && (
                                                <div className="text-xs text-muted-foreground">
                                                    {log.resourceType}:{" "}
                                                    {log.resourceName}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            <span
                                                className={getStatusColor(
                                                    log.status
                                                )}
                                            >
                                                {log.status || "N/A"}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            <span
                                                className={getSeverityColor(
                                                    log.severity
                                                )}
                                            >
                                                {log.severity || "N/A"}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="border-t pt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={pageSize}
                        totalItems={pagination.totalCount}
                    />
                </div>
            )}
        </div>
    );
}
